import React, { createContext, useContext, useState, useEffect } from 'react';
import { isFirebaseConfigured } from '../firebase/config';
import {
  uploadGalleryEntry,
  saveGalleryLinkDocument,
  deleteGalleryStorageFile,
} from '../firebase/galleryService';

const ImageContext = createContext();

export const useImages = () => {
  const context = useContext(ImageContext);
  if (!context) {
    throw new Error('useImages must be used within an ImageProvider');
  }
  return context;
};

export const useGallery = () => {
  const context = useContext(ImageContext);
  if (!context) {
    throw new Error('useGallery must be used within an ImageProvider');
  }
  return context;
};

export const ImageProvider = ({ children }) => {
  const [images, setImages] = useState([]);
  const [showToast, setShowToast] = useState({ show: false, message: '', type: '' });

  // Load images from localStorage on mount
  useEffect(() => {
    const savedImages = localStorage.getItem('galleryImages');
    if (savedImages) {
      const parsedImages = JSON.parse(savedImages);
      setImages(parsedImages);
    } else {
      // Default images
      const defaultImages = [
        {
          id: 1,
          title: 'Modern Living Space',
          category: 'Living',
          imageUrl: 'https://lh3.googleusercontent.com/aida-public/AB6AXuC2Gbn0IJg0rCrTjCIYMabD86Rxx8kB1eI0CSXrvRoI1w-e4-M5XEK7pQD1GCXRrP1Ovtm5hRk83jPXJsKm8-PUGyX4T5tDNIU96RhcI4ews7ER3_UAj0c440ux1T2aPEHr_5ln_lPU_y1XOUNRy3bgRczZ0GMN9IeC1uC8PB7QGmdffWsTBTP_c4pQClLnczyA0mHGGcTPlfKy0C-DNRfzW41DvkkVlFNIK2SwF3ABDopXKMU9dAIThZCDd4XdhSjEj96pHJkZY_bA'
        },
        {
          id: 2,
          title: 'Serene Bedroom Retreat',
          category: 'Bedroom',
          imageUrl: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAOjNQ5-AjF4G4D6WxhZ03g0OSPhqASmr05dUY0j3NtN7zjr5mz1B0t8I8GVtWpkx2ULcAxqTZnVYe_pMCKVL0UPQBknMjr3BoeJkGzecYWB_d2HVdpmFyCaBlYbDrj9PeUXqGngcwc8Zx20h_LtBxlWzEjjHmS5JCIDf4ENqQkh4cAb8rU35wvh09ZJxk_uc6i9v--902aoUr6EFmlQX1wZ__4GM15BJvCtW2NbNrGTq1l55k1XSy06eZ7CwJwjhpLNfO8ft-ThN88'
        },
        {
          id: 3,
          title: 'Contemporary Kitchen Design',
          category: 'Kitchen',
          imageUrl: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAlx3tRaV6p1Z0RzKC7IvkjZQtghnRAxSMvOEC1CTZAt_m6y89IWKPpfTNr7QnOO4bDybNl7PFSFsgEpjEy6lwo__Dpm8H_5J21KP7GaGTpQI2Vhi_DFqBATzarKBo_j_IbpvnpllMP8YutG8pQyUZr8lLySX0-Wp0Cj-9iM7j2dTPNt4kCfodmJXP1Z9ZhcabPYjloWJk8nt2Uc1YhEkLuv7rFwBfaYDCMFzasl-cpKCCL9cFEKLXaZE6Av8E4QZ1Us_Jnrqkn_5Q8'
        },
        {
          id: 4,
          title: 'Executive Office Space',
          category: 'Office',
          imageUrl: 'https://lh3.googleusercontent.com/aida-public/AB6AXuASXIxe3e3_CVcJqAQiXXoCTYhlUWBFrkHhS3RvF2HVqF9XnaNJ7-_3gSfaVVhcHbAbqClIf_2pndp1dVJF6tQOfRdeJBVflXiic4d4cOL3r35FJ9iuXOp1RzNpeZvY4EVjzOoXEGHcNxSc62eYGV5Y60mqhx6191NyMPIUZ2InxTSeR7ewjEopwaCKpa7dghX7Fo3Xup6eOUvo9u-mnLlOD7Zioaz7JMlIxfj6c7CrnaMJx7nNOz3IYjtD48G93VNmo75NlcjItxgS'
        },
        {
          id: 5,
          title: 'Luxury Bathroom Suite',
          category: 'Bathroom',
          imageUrl: 'https://lh3.googleusercontent.com/aida-public/AB6AXuBSQIWC_9b-9Gjt-ZNGBs587OPT_qMy5VqudzWVqUI_4yXpJr_vMqfHqsPBLjDqRB-N5vAfFk-vxuRgE1UGgUD-JTZHiO5u0vW55uTnG9JIumYKaJFR3sQNBWFKaKsycDW9l91c0glxzkJiUPIGibUIdVl1FO6wO1xiRHd96W4Oog1Uf86cUsvu5T0DtoVDNCY1JTTWXyjIhKcpF6HcbjOvARl6r8ZptiFfG4yplfajJQCgsOuP9GRQxa0_KY8HAdjNQHIdbTaFRVF5'
        }
      ];
      setImages(defaultImages);
      localStorage.setItem('galleryImages', JSON.stringify(defaultImages));
    }
  }, []);

  // Save images to localStorage whenever they change
  useEffect(() => {
    if (images.length > 0) {
      localStorage.setItem('galleryImages', JSON.stringify(images));
    }
  }, [images]);

  const showToastNotification = (message, type) => {
    setShowToast({ show: true, message, type });
    setTimeout(() => {
      setShowToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const addImage = (newImage) => {
    const finalizeLocal = () => {
      const imageWithId = {
        ...newImage,
        id: Date.now(),
      };
      setImages((prev) => [...prev, imageWithId]);
      showToastNotification('Image added successfully!', 'success');
    };

    const run = async () => {
      if (!isFirebaseConfigured()) {
        finalizeLocal();
        return;
      }
      try {
        if (newImage.imageUrl?.startsWith('data:')) {
          const uploaded = await uploadGalleryEntry({
            title: newImage.title,
            category: newImage.category,
            dataUrl: newImage.imageUrl,
          });
          setImages((prev) => [...prev, uploaded]);
          showToastNotification('Image added successfully!', 'success');
          return;
        }
        if (newImage.imageUrl?.startsWith('http')) {
          const saved = await saveGalleryLinkDocument({
            title: newImage.title,
            category: newImage.category,
            imageUrl: newImage.imageUrl,
          });
          setImages((prev) => [...prev, saved]);
          showToastNotification('Image added successfully!', 'success');
          return;
        }
      } catch (e) {
        console.error('Gallery Firebase sync error:', e);
        showToastNotification('Cloud sync failed; saving locally only.', 'error');
      }
      finalizeLocal();
    };

    void run();
  };

  const deleteImage = (imageId) => {
    const image = images.find((img) => img.id === imageId);
    const title = image?.title || 'Image';

    const run = async () => {
      try {
        if (isFirebaseConfigured() && image?.storagePath) {
          await deleteGalleryStorageFile(image.storagePath);
        }
      } catch (e) {
        console.error('Gallery storage delete error:', e);
      }
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      showToastNotification(`"${title}" deleted successfully!`, 'error');
    };

    void run();
  };

  // Get latest images (for home page)
  const getLatestImages = (limit = 6) => {
    return [...images].sort((a, b) => b.id - a.id).slice(0, limit);
  };

  // Get images by category
  const getImagesByCategory = (category) => {
    if (category === 'All') {
      return images;
    }
    return images.filter(img => img.category === category);
  };

  const value = {
    images,
    galleries: images, // Add galleries alias for AdminDashboard
    addImage,
    deleteImage,
    getLatestImages,
    getImagesByCategory,
    showToast
  };

  return (
    <ImageContext.Provider value={value}>
      {children}
      {showToast.show && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ${
            showToast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}
        >
          {showToast.message}
        </div>
      )}
    </ImageContext.Provider>
  );
};
